import { Button, Icon } from 'components/common';
import CoinProgressBar from 'components/promotion/CoinProgressBar';
import CoinPurchaseAnimation, { CoinPurchaseAnimationRef } from 'components/promotion/CoinPurchaseAnimation';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Coin777Data } from 'types/bottomsheet';

const PRODUCT_ID = 'ccoin_777';
const JACKPOT_IMG = '/imgweb/promo/jackpot.png';

const Coin777PromotionContent = () => {
  const { close, sheets, updateOptions, updateData } = useBottomSheet();
  const sheet = sheets.Coin777;
  const [details, setDetails] = useState<any>(null);
  const [progressCompleted, setProgressCompleted] = useState(false);
  const { imageLoaded, onPurchaseError, layout = 'fullscreen' } = sheet.data as Coin777Data;
  const isSheet = layout === 'sheet';

  const coinPurchaseAnimationRef = useRef<CoinPurchaseAnimationRef>(null);

  const preloadImage = useCallback(() => {
    updateOptions(sheet.id, {
      containerStyle: {
        animation: 'highlight 2s infinite',
        animationFillMode: 'forwards',
        backgroundSize: '800% 100%',
        backgroundImage: 'linear-gradient(-75deg, transparent 0%, transparent 12.5%, rgba(255,255,255, 0.1) 17.5%, transparent 30%)',
      },
    });

    const img = new Image();
    img.src = JACKPOT_IMG;
    img.onload = () => {
      setProgressCompleted(true);
      setTimeout(() => {
        updateData(sheet.id, { imageLoaded: true });
      }, 700);
      updateOptions(sheet.id, {
        containerStyle: { animation: 'none' },
      });
    };
  }, [sheet.id, updateOptions, updateData]);

  useEffect(() => {
    const details = PageShop.getProductDetails(PRODUCT_ID);
    setDetails(details);

    PurchaseManager.attach(function (err: any, coin: any) {
      if (err) {
        onPurchaseError?.(err);
        return;
      }

      logPurchase('accept');
      // 시트형은 예전처럼 구매 즉시 닫는다
      if (isSheet) {
        close(sheet.id, { skipConfirm: true, data: { purchased: true } });
        return;
      }
      coinPurchaseAnimationRef.current?.show().then(() => {
        close(sheet.id, { skipConfirm: true, data: { purchased: true } });
      });
    });
    return () => {
      PurchaseManager.detach();
    };
  }, [close, sheet.id, coinPurchaseAnimationRef, onPurchaseError, isSheet]);

  useEffect(() => {
    if (!isSheet) preloadImage();
  }, [preloadImage, isSheet]);

  const logPurchase = (action: 'accept' | 'cancel') => {
    const promoCount: number = log?.lucky777Promo?.count ?? 1;
    const baObj = {
      fbUid: myinfo.fbUid,
      country: myinfo.country,
      device: Device.name,
      deviceId: BResource.getDeviceId(),
      promoType: '777Coin',
      promoName: '777Coin',
      promoCount,
      action,
    };
    ba('promo', undefined, baObj);
  };

  const purchase = () => {
    PurchaseManager.buyCoin(PRODUCT_ID);

    ba('fcm-popup', undefined, {
      popup: sheet.id,
      actionType: 'clicked-purchase',
      productId: PRODUCT_ID,
      result: sheet.data,
      fbUid: myinfo.fbUid,
      country: myinfo.country,
      device: Device.name,
    });
  };

  const cancel = () => {
    close(sheet.id, { skipConfirm: false });
    logPurchase('cancel');
  };

  if (isSheet) {
    return <SheetLayout price={details?.price} onPurchase={purchase} />;
  }

  return (
    <div className="flex h-full w-full flex-col items-center">
      <div className="flex h-[56px] w-full">
        <Icon name="icon/ic_close.svg" onClick={cancel} color="filter-base-ic-white" />
      </div>
      <CoinProgressBar loaded={progressCompleted} />
      <div className="text-center" style={{ color: '#FFC539', fontSize: '56px', fontWeight: 700, lineHeight: '1' }}>
        {brsc.give_param_coin.replace('$param', '277')}
      </div>
      <div className="relative mb-auto mt-6 flex w-full flex-col items-center justify-center">
        <img className="h-[80dvw] w-[80dvw]" alt="" src={JACKPOT_IMG} loading="eager" style={{ visibility: imageLoaded ? 'visible' : 'hidden' }} />
        <PriceInfo price={details?.price} />
      </div>
      <div className="space-between flex w-full flex-col items-center">
        <div className="mb-2 typography-body-sm text-dialog-txt-secondary">{brsc.eave_page_warning}</div>
        <Button onClick={purchase} style={{ text: brsc.purchase, background: '#FFC539', fontColor: 'black' }} className="w-full" />
      </div>
      <CoinPurchaseAnimation ref={coinPurchaseAnimationRef} />
    </div>
  );
};

/** 예전 Lucky777 바텀시트 화면 (dialog.css 의 lucky-777 클래스) */
const SheetLayout = ({ price, onPurchase }: { price?: string; onPurchase: () => void }) => {
  const html = (__html: string) => ({ dangerouslySetInnerHTML: { __html } });
  const desc2 = <div key="desc2" className="lucky-777-desc2" {...html(brsc.lucky_777_contents3)} />;
  const desc3 = <div key="desc3" className="lucky-777-desc3" {...html(brsc.lucky_777_contents4)} />;
  // 아랍어와 러시아어는 description 순서를 바꾼다
  const reversed = ['ar', 'ru'].includes(BResource.getSelectedLocale());

  return (
    <div className="flex w-full flex-col items-center text-center">
      <div className="lucky-777-badge">SURPRISE EVENT</div>
      <div className="lucky-777-title" {...html(brsc.lucky_777_title)} />
      <div className="lucky-777-desc" {...html(brsc.lucky_777_contents1)} />
      <img className="lucky-777-img" alt="" src={JACKPOT_IMG} />
      {reversed ? [desc3, desc2] : [desc2, desc3]}
      <div className="lucky-777-amount-cont flexcenter" style={{ marginTop: 4 }}>
        <div className="lucky-777-original-amount">
          500<div></div>
        </div>
        <div style={{ width: 6 }} />
        <div className="lucky-777-amount">
          <span>777</span> coin
        </div>
        <div className="lucky-777-arrow">&#x2192;</div>
        <div className="lucky-777-price">{price}</div>
      </div>
      <Button onClick={onPurchase} style={{ text: brsc.lucky_777_btn, color: 'btn-bg-primary', fontColor: 'btn-txt-primary' }} className="mt-6 w-full" />
    </div>
  );
};

const PriceInfo = ({ price }: { price: number }) => {
  if (!price) return <></>;

  return (
    <div className="gr-box absolute bottom-0 flex flex-col items-center justify-center gap-2 rounded-lg px-6 py-2">
      <div className="typography-title-md" style={{ color: '#FFC539' }}>
        777Coins
      </div>
      <div className="text-white">{price}</div>
    </div>
  );
};

export default Coin777PromotionContent;
